import * as React from "react";
import { useEffect, useState, createRef } from "react";
import styled from "styled-components";
import {
  OriginThemeProvider,
  TrackingProvider
} from "@origin-digital/ods-core";
import { LinkComponentProps } from "@origin-digital/ods-types";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";

import { fontStyles, resetStyles, iconFontStyles } from "../../styles/global";
import { Footer } from "../Footer";
import { SacVersionToggle } from "../SacVersionToggle";
import { KrakenAppContent, MyAccountAppContent } from "../AppContent";
import { SuppressAndroidInstallPrompt } from "../SuppressAndroidInstallPrompt";
import { CovidBanner, CrisisBanner } from "../Banner";

const MeshLink = React.forwardRef<any, LinkComponentProps>((props, ref) => {
  const { href, onClick } = parseMarkdownEvent(props.href);
  return (
    <a
      ref={ref}
      {...props}
      href={href}
      onClick={e => {
        onClick && onClick(e);
        return props.onClick && props.onClick(e);
      }}
    />
  );
});

const ContentWrapper = styled.div`
  flex: 1 0 auto;
  ${({ background }: { background: "gray" | "white" | undefined }) => {
    if (background === "gray") return `background-color: rgb(241, 241, 241);`;
    if (background === "white") return `background-color: white;`;
    return "";
  }};
`;

const FooterWrapper = styled.div`
  flex-shrink: 0;
`;

//TODO: Type this exactly to match what tal-core passes on.
export interface AppProps {
  title: string;
  nav?: any;
  footer?: any;
  actionBar?: any;
  background?: "gray" | "white";
  children?: React.ReactNode;
  presets?: string[];
  platform?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  showNativeAppAd?: boolean;
  authenticated?: boolean;
  initSAC?: boolean;
  liveChat?: any;
  aemContent?: any;
  channel?: string;
}

interface OetalApplicationRootProps {
  children?: React.ReactNode;
  showFooter?: boolean;
}

// dev notes: OetalApplicationRoot sets a default height style to push the
// footer (if any) off the viewport and when an app is populated into it in the
// browser, the height style is removed.  This is done so the layout shift of
// the app forcing the footer down does not impact the core web-vital CLS
// figure.  As this default style is applied during SSR the resultant HTML will
// have that height in it.

// For applications that are longer than the viewport, the footer CLS shift is
// quite significant. Taking the footer off the viewport reduces it a lot.  For
// applications that are smaller than the viewport then the CLS reduction is not
// so much - but not many apps are smaller than the viewport.

// Note that this is only done when the footer is actually to be displayed and
// there are no children.  The only circumstance when there will be children is
// when a full-page SSR is underway - like with the home page.

// To detect an application being rendered as a child, we use a
// MutationObserver.  As soon as a child exists the height style is removed and
// the observer disconnected.
const OetalApplicationRoot = ({
  children,
  showFooter
}: OetalApplicationRootProps) => {
  const [heightStyle, setHeightStyle] = useState(
    showFooter && !children ? { height: "2500px" } : {}
  );
  const ref = createRef<HTMLDivElement>();
  let observer;

  useEffect(() => {
    if (showFooter && !children) {
      const { current } = ref;
      const callback = (mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            setHeightStyle({});
            observer.disconnect();
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(current, {
        attributes: false,
        childList: true,
        subtree: false
      });
    }
  }, []);
  return (
    <div id="oetal-application-root" ref={ref} style={heightStyle}>
      {children ? children : null}
    </div>
  );
};

const App = ({
  nav,
  footer,
  actionBar,
  title,
  background,
  children,
  platform,
  presets,
  showHeader = true,
  showFooter = true,
  showNativeAppAd = true,
  authenticated = false,
  initSAC = false,
  liveChat = undefined,
  aemContent = false,
  channel
}: AppProps) => {
  const isKraken =
    platform === "kraken" || (presets && presets.includes("kraken"));
  const isHomePage = !!aemContent;
  const isNative = channel === "native";

  return (
    <>
      {fontStyles()}
      {resetStyles()}
      {iconFontStyles()}

      <OriginThemeProvider
        linkComponent={MeshLink}
        generatorClassNameOptions={{ seed: "tal" }}
      >
        <TrackingProvider onTrackingCapture={trackingProviderCaptureClick}>
          <ContentWrapper data-id="tal-content-wrapper" background={background}>
            {!showNativeAppAd && <SuppressAndroidInstallPrompt />}

            {!isNative && <CrisisBanner />}
            {!isNative && isHomePage && <CovidBanner />}

            {isKraken ? (
              <KrakenAppContent
                title={title}
                showHeader={showHeader}
                actionBar={actionBar}
              />
            ) : (
              <SacVersionToggle initSAC={initSAC} authenticated={authenticated}>
                {context => {
                  const { customerCode, premiseId, properties, isNative } =
                    context || {};

                  return (
                    <MyAccountAppContent
                      actionBar={actionBar}
                      authenticated={authenticated}
                      customerCode={customerCode}
                      isNative={isNative}
                      liveChat={liveChat}
                      onSelectPremise={premiseId => {
                        context.actions.setPremiseId(premiseId);
                      }}
                      nav={nav}
                      showHeader={showHeader}
                      premiseId={premiseId}
                      properties={properties}
                      title={title}
                    />
                  );
                }}
              </SacVersionToggle>
            )}
            <OetalApplicationRoot showFooter={showFooter}>
              {children ? children : null}
            </OetalApplicationRoot>
          </ContentWrapper>
          {showFooter ? (
            <FooterWrapper>
              <Footer {...footer} />
            </FooterWrapper>
          ) : null}
        </TrackingProvider>
      </OriginThemeProvider>
    </>
  );
};

const Component = App;

export { Component as App };
